/**
 * QCP (Chinese Poker) client-side protocol types.
 *
 * Hand-mirrored from:
 *   QChinesePoker/packages/qcp-types/src/events.ts
 *
 * The TMA cannot import the server package directly, so this file is the
 * authoritative client-side copy. Keep in sync with the server contract
 * whenever the server emits/receives new events or changes payload shapes.
 *
 * Mirror policy: same string values, same payload shapes, no Zod dependency
 * (the client validates via TypeScript only).
 */

/* ------------------------------------------------------------------ */
/* Event name maps                                                     */
/* ------------------------------------------------------------------ */

/** Events sent by the client → server. */
export const ClientEvents = {
  Join: 'match:join',
  ClientSeed: 'deck:clientSeed',
  Place: 'place:submit',
  PlaceFl: 'place:fl',
  Resign: 'match:resign',
  MmEnqueue: 'mm:enqueue',
} as const;

/** Events sent by the server → client. */
export const ServerEvents = {
  Commit: 'deck:commit',
  Deal: 'deal:cards',
  State: 'match:state',
  PlaceApplied: 'place:applied',
  Reveal: 'hand:reveal',
  Ended: 'match:ended',
  Clock: 'clock:update',
  Err: 'error',
  MmMatched: 'mm:matched',
  InviteAccepted: 'qcp:invite:accepted',
  HandStart: 'hand:start',
  HandSettled: 'hand:settled',
  MatchResult: 'match:result',
} as const;

/* ------------------------------------------------------------------ */
/* Primitive types                                                     */
/* ------------------------------------------------------------------ */

/** A single playing card. r = rank (2–14), s = suit. */
export type Card = { r: number; s: 'c' | 'd' | 'h' | 's' };

/** The two seat identifiers (A = creator/first player, B = second player). */
export type Seat = 'A' | 'B';

/** The three rows of an OFC board, from weakest (front 3) to strongest (back 5). */
export type Row = 'front' | 'middle' | 'back';

/** A single player's arranged board (3 rows, each partially filled). */
export type Board = {
  front: Card[];
  middle: Card[];
  back: Card[];
};

/* ------------------------------------------------------------------ */
/* Projected game state (server → client via ServerEvents.State)      */
/* ------------------------------------------------------------------ */

/**
 * The full projected state pushed to a connected client.
 *
 * `public`  — visible to all: both boards, scores, phase metadata.
 * `me`      — private to this socket: current draw cards + discards so far.
 * `opponents` — masked view of the other seat's hidden state (counts only).
 */
export type ProjectedState = {
  public: {
    boards: Record<Seat, Board>;
    scores: Record<Seat, number>;
    turn: Seat;
    round: number;
    deckRemaining: number;
    commitHash: string | null;
    phase: string;
    inFantasyland: Record<Seat, boolean>;
  };
  me: {
    draw: Card[];
    discards: Card[];
  };
  opponents: Partial<Record<Seat, { drawCount: number; discardCount: number }>>;
};

/* ------------------------------------------------------------------ */
/* Server → client payload types                                       */
/* ------------------------------------------------------------------ */

/** Payload for ServerEvents.Deal — the cards dealt to this player for the round. */
export type DealPayload = { cards: Card[] };

/** Payload for ServerEvents.PlaceApplied — result of a placement accepted by the server. */
export type PlaceAppliedPayload = {
  seat: Seat;
  placed: { card: Card; row: Row }[];
  discardCount: number;
};

/** Payload for ServerEvents.Reveal — the deck secret revealed at end of hand. */
export type RevealPayload = {
  serverSeed: string;
  deckOrder: Card[];
  discards: Record<Seat, Card[]>;
};

/** Payload for ServerEvents.Ended — hand/match result with net score deltas. */
export type EndedPayload = {
  net: Record<Seat, number>;
  winner: Seat | null;
  reason: string;
};

/** Payload for ServerEvents.Clock — current per-seat banked time remaining.
 *  `owner` is null before the clock has started (no seat is on the move yet). */
export type ClockPayload = {
  banked: Record<Seat, number>;
  owner: Seat | null;
};

/** Payload for ServerEvents.Err — structured error from the gameserver. */
export type ErrorPayload = { code: string; message: string };

/** Payload for ServerEvents.MmMatched — the matchmaking queue found a match. */
export type MmMatchedPayload = { matchId: string };

/**
 * Payload for ServerEvents.InviteAccepted — emitted to the invite creator
 * (always seat A) when their opponent accepts the invite link.
 */
export type InviteAcceptedPayload = {
  code: string;
  matchId: string;
  seat: 'A';
  opponent: { userId: string; displayName: string; elo: number };
};

/* ------------------------------------------------------------------ */
/* Client → server intent types                                        */
/* ------------------------------------------------------------------ */

/**
 * Payload for ClientEvents.Join — join (or create-on-first-join) a match.
 * `vsBot: true` requests a practice match against the heuristic bot (seat B is
 * the bot). Omitted/false = a human-vs-human match (pre-assigned via invite or
 * matchmaking, or first-come on a shared matchId in dev).
 */
export type Join = {
  matchId: string;
  vsBot?: boolean;
};

/**
 * Payload for ClientEvents.Place — the client's placement decision for one round.
 * Up to 5 placements (Pineapple draws 3 in most rounds; opening round may vary).
 * `discard` is null if no card is discarded (front row's initial deal).
 */
export type PlaceSubmit = {
  matchId: string;
  placements: { card: Card; row: Row }[];
  discard: Card | null;
};

/**
 * Payload for ClientEvents.MmEnqueue — join the ranked matchmaking queue.
 * Server-enforced: `bank` must be a positive int, `inc` a non-negative int, AND
 * the pair must match a configured DEFAULT_BUCKET ({60,5} or {120,10}) or it
 * enqueues but never pairs. The queue UI must send only configured buckets.
 */
export type MmEnqueue = {
  bank: number;
  inc: number;
};

/**
 * Payload for ClientEvents.PlaceFl — a Fantasyland seat sets its full 13-card
 * board in a single move (no per-round draws). `placements` is the complete
 * board (front 3 / middle 5 / back 5); `discards` are the leftover FL cards
 * (flCardCount − 13, i.e. 1..4 in Pineapple FL).
 */
export type PlaceFlSubmit = {
  matchId: string;
  placements: Board;
  discards: Card[];
};

/* ------------------------------------------------------------------ */
/* Multi-hand server → client payload types                            */
/* ------------------------------------------------------------------ */

/**
 * Payload for ServerEvents.HandStart — a new hand begins.
 * Carries the asymmetric Fantasyland state for the upcoming hand:
 * which seat is in FL and how many cards each FL seat is dealt
 * (`flCardCount` 0 = a normal, non-FL seat).
 */
export type HandStartPayload = {
  handNumber: number;
  button: Seat;
  inFantasyland: Record<Seat, boolean>;
  flCardCount: Record<Seat, number>;
};

/** Outcome of one row for a seat against the other: 1 won, 0 tied, -1 lost. */
export type LineResult = -1 | 0 | 1;

/**
 * One seat's side of the settlement, computed by the SERVER (QChinesePoker `@qcp/rules` `breakdownPair`, QCP#15, served on qchinesepoker-stg from
 * 2026-10-17 01:54Z; mirror of `SeatBreakdownSchema` in `packages/qcp-types/src/events.ts` @ 9e799e55). The client reads it, never recomputes it.
 *  - `foul`   — this seat fouled;
 *  - `scoop`  — true only when this seat wins all three rows and neither seat fouls;
 *  - `lines`  — this seat against the other, per row; ABSENT when either seat fouls (no row is compared);
 *  - `royalties` — the royalties this seat is PAID: all zero when the seat fouls.
 */
export type SeatBreakdown = {
  foul: boolean;
  scoop: boolean;
  lines?: { front: LineResult; middle: LineResult; back: LineResult };
  royalties: { front: number; middle: number; back: number; total: number };
};

/**
 * Payload for ServerEvents.HandSettled — a hand has settled.
 * Carries the per-hand net deltas, the cumulative running net across the set,
 * the optional deck reveal (null when not revealed), and the next-hand
 * Fantasyland carry-over.
 * `breakdown` is OPTIONAL: it is absent on forfeit/abandon and whenever `reveal` is null, and a server older than QCP#15 never sends it. Every reader
 * treats a missing or malformed `breakdown` as «no breakdown» (`lib/qcp/resoconto.ts`), never as an error.
 */
export type HandSettledPayload = {
  handNumber: number;
  netA: number;
  netB: number;
  runningNetA: number;
  runningNetB: number;
  handWinner: Seat | null;
  reveal: {
    boardA: Board;
    boardB: Board;
    serverSeed: string;
    deckOrder: Card[];
    discards: Record<Seat, Card[]>;
  } | null;
  breakdown?: Record<Seat, SeatBreakdown>;
  fantasylandNext: Record<Seat, boolean>;
  flCardCountNext: Record<Seat, number>;
};

/**
 * Payload for ServerEvents.MatchResult — the match (set) is over; emitted once.
 * `winner` is null on a draw. `elo` is null when a bot is involved (no rating
 * change); otherwise it carries the before/after/delta rating for each seat.
 */
export type MatchResultPayload = {
  matchId: string;
  finalNetA: number;
  finalNetB: number;
  winner: Seat | null;
  handsPlayed: number;
  reason: 'complete' | 'draw' | 'forfeit' | 'abandoned' | 'resign';
  elo: {
    A: { before: number; after: number; delta: number };
    B: { before: number; after: number; delta: number };
  } | null;
};

/* ------------------------------------------------------------------ */
/* Handshake (T1a)                                                     */
/* ------------------------------------------------------------------ */

/**
 * The Socket.IO handshake `auth` of the gameserver (`apps/qcp-gameserver/src/server.ts`, middleware `io.use`): the single-use
 * TICKET from the Limulo BFF and nothing else — no Bearer, no dev bypass. Never in a query string, a header, a log or a storage.
 */
export type QcpHandshakeAuth = { ticket: string };

/** `connect_error` message for a ticket that is expired, already spent or unknown (the three are indistinguishable on purpose). */
export const QCP_INVALID_TICKET = 'invalid_ticket';
